import { useQueries, useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useEffect, useState } from 'react'
import { boardQueryOptions } from '@/entities/board'
import {
  boardGanttQueryOptions,
  boardTasksQueryOptions,
  type Task,
  taskQueryOptions,
} from '@/entities/task'
import { ApiError } from '@/shared/api'
import { cn } from '@/shared/lib'
import { Button } from '@/shared/ui/button'
import { DepartmentBadges } from '@/shared/ui/department-badge'
import { EmptyState } from '@/shared/ui/empty-state'
import { Icon } from '@/shared/ui/icon'
import { PageHeader } from '@/shared/ui/page-header'
import { StatusScreen } from '@/shared/ui/status-screen'
import { buildWindow, GanttChart, type Scale, shiftAnchor, startOfDay } from '@/widgets/gantt'

interface GanttSearch {
  scale?: 'month'
  anchor?: string
}

export const Route = createFileRoute('/gantt/$boardId')({
  component: GanttPage,
  head: () => ({ meta: [{ title: 'Гант · STIT Task' }] }),
  // Масштаб и якорь периода живут в URL (шарятся, переживают перезагрузку).
  // Дефолты (неделя, сегодня) в URL не пишем — чистый адрес по умолчанию.
  validateSearch: (s: Record<string, unknown>): GanttSearch => ({
    ...(s.scale === 'month' ? { scale: 'month' as const } : {}),
    ...(typeof s.anchor === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(s.anchor)
      ? { anchor: s.anchor }
      : {}),
  }),
})

const btnSecondarySm =
  'inline-flex items-center justify-center gap-2 rounded-control border border-border-strong px-3 pt-[9px] pb-[7px] font-mono text-xs font-medium uppercase tracking-wide text-text-primary transition-colors hover:border-text-primary hover:bg-bg-elevated'

function useNow(): Date {
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 60_000)
    return () => clearInterval(id)
  }, [])
  return now
}

function toDateStr(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)
const monthYearFmt = new Intl.DateTimeFormat('ru-RU', { month: 'long', year: 'numeric' })
const dayMonthFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' })
const dayFmt = new Intl.DateTimeFormat('ru-RU', { day: 'numeric' })

function periodLabel(anchor: Date, scale: Scale): string {
  if (scale === 'month') return cap(monthYearFmt.format(anchor))
  const from = startOfDay(anchor)
  const monday = new Date(
    from.getFullYear(),
    from.getMonth(),
    from.getDate() - ((from.getDay() + 6) % 7),
  )
  const sunday = new Date(monday.getFullYear(), monday.getMonth(), monday.getDate() + 6)
  const sameMonth = monday.getMonth() === sunday.getMonth()
  return sameMonth
    ? `${dayFmt.format(monday)}–${dayMonthFmt.format(sunday)}`
    : `${dayMonthFmt.format(monday)} – ${dayMonthFmt.format(sunday)}`
}

const SKELETON_ROWS = ['r1', 'r2', 'r3', 'r4', 'r5']

function GanttSkeleton() {
  return (
    <div className="overflow-hidden rounded-card border border-border-subtle bg-bg-surface">
      <div className="h-12 border-b border-border-subtle bg-bg-elevated/40" />
      {SKELETON_ROWS.map((r) => (
        <div
          key={r}
          className="flex h-[52px] items-center gap-3 border-b border-border-subtle px-4"
        >
          <div className="h-3 w-40 animate-pulse rounded-control bg-bg-elevated" />
          <div className="h-4 flex-1 animate-pulse rounded-control bg-bg-elevated" />
        </div>
      ))}
    </div>
  )
}

function GanttPage() {
  const { boardId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const now = useNow()

  const scale: Scale = search.scale === 'month' ? 'month' : 'week'
  const anchor = search.anchor ? new Date(`${search.anchor}T00:00:00`) : now
  const win = buildWindow(anchor, scale)

  const ganttQ = useQuery(boardGanttQueryOptions(boardId))
  const tasksQ = useQuery(boardTasksQueryOptions(boardId))
  const boardQ = useQuery(boardQueryOptions(boardId))

  const gantt = ganttQ.data
  const boardTaskIds = new Set((tasksQ.data ?? []).map((t) => t.id))
  const externalIds = gantt
    ? [
        ...new Set(
          gantt.dependencies
            .filter((d) => !boardTaskIds.has(d.dependsOnId))
            .map((d) => d.dependsOnId),
        ),
      ]
    : []

  const extTaskQs = useQueries({ queries: externalIds.map((id) => taskQueryOptions(id)) })
  const extTasks = extTaskQs.map((q) => q.data).filter((t): t is Task => !!t)
  const extBoardIds = [...new Set(extTasks.map((t) => t.boardId))]
  const extBoardQs = useQueries({ queries: extBoardIds.map((id) => boardQueryOptions(id)) })

  // Cross-board блокер-чип показывает один отдел — берём первый из набора доски.
  const deptByBoard = new Map<string, string | null>()
  for (const q of extBoardQs) {
    if (q.data) {
      deptByBoard.set(q.data.id, q.data.departments[0] ?? null)
    }
  }
  const externals = new Map<string, { title: string; department: string | null }>()
  for (const t of extTasks) {
    externals.set(t.id, { title: t.title, department: deptByBoard.get(t.boardId) ?? null })
  }

  const setSearch = (next: GanttSearch) => void navigate({ search: next })
  const withAnchor = (a: Date | null): GanttSearch => ({
    ...(scale === 'month' ? { scale: 'month' as const } : {}),
    ...(a ? { anchor: toDateStr(a) } : {}),
  })
  const setScale = (s: Scale) =>
    setSearch({
      ...(s === 'month' ? { scale: 'month' as const } : {}),
      ...(search.anchor ? { anchor: search.anchor } : {}),
    })
  const shift = (dir: -1 | 1) => setSearch(withAnchor(shiftAnchor(anchor, scale, dir)))
  const goToday = () => setSearch(withAnchor(null))

  const header = (
    <PageHeader
      title="Гант"
      status={boardQ.data ? <DepartmentBadges names={boardQ.data.departments} /> : undefined}
      actions={
        <Link to="/boards/$boardId" params={{ boardId }} className={btnSecondarySm}>
          <Icon name="kanban" size={14} />
          Доска
        </Link>
      }
    />
  )

  if (ganttQ.isPending || tasksQ.isPending || boardQ.isPending) {
    return (
      <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
        {header}
        <div className="mt-6">
          <GanttSkeleton />
        </div>
      </div>
    )
  }

  if (ganttQ.isError || tasksQ.isError || boardQ.isError) {
    const notFound = boardQ.error instanceof ApiError && boardQ.error.status === 404
    return (
      <StatusScreen
        title={notFound ? 'Доска не найдена' : 'Не удалось построить Гант'}
        message={
          notFound
            ? 'Возможно, она удалена или у вас нет доступа.'
            : 'Попробуйте обновить страницу.'
        }
      >
        {!notFound && (
          <Button
            variant="secondary"
            onClick={() => {
              void ganttQ.refetch()
              void tasksQ.refetch()
              void boardQ.refetch()
            }}
          >
            Повторить
          </Button>
        )}
        <Link
          to="/boards/$boardId"
          params={{ boardId }}
          className="inline-flex items-center rounded-control px-5 py-3 text-sm text-text-secondary transition-colors hover:text-text-primary"
        >
          К доске
        </Link>
      </StatusScreen>
    )
  }

  const tasks = tasksQ.data
  const hasAnyDue = tasks.some((t) => t.dueDate)

  return (
    <div className="container-x px-4 py-8 sm:px-6 sm:py-10">
      {header}

      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Предыдущий период"
            onClick={() => shift(-1)}
            className="rounded-control p-2 text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
          >
            <Icon name="chevron-left" size={16} />
          </button>
          <span className="min-w-28 text-center text-sm text-text-primary sm:min-w-[8.5rem]">
            {periodLabel(anchor, scale)}
          </span>
          <button
            type="button"
            aria-label="Следующий период"
            onClick={() => shift(1)}
            className="rounded-control p-2 text-text-secondary transition-colors hover:bg-bg-elevated hover:text-text-primary"
          >
            <Icon name="chevron-right" size={16} />
          </button>
        </div>
        <button
          type="button"
          onClick={goToday}
          disabled={!search.anchor}
          className="rounded-control px-3 py-1.5 text-xs text-text-secondary transition-colors hover:text-text-primary disabled:opacity-40"
        >
          Сегодня
        </button>
        {/* Скрыт на узком экране: ряд там переносится и разделитель осиротел бы. */}
        <span aria-hidden className="mx-1 hidden h-4 w-px bg-border-subtle sm:block" />
        <div className="flex items-center gap-1">
          {(['week', 'month'] as const).map((s) => (
            <button
              key={s}
              type="button"
              aria-pressed={scale === s}
              onClick={() => setScale(s)}
              className={cn(
                'rounded-pill border px-3 py-1 font-mono text-xs uppercase tracking-wide transition-colors',
                scale === s
                  ? 'border-border-strong bg-bg-elevated text-text-primary'
                  : 'border-border-default text-text-secondary hover:text-text-primary',
              )}
            >
              {s === 'week' ? 'Неделя' : 'Месяц'}
            </button>
          ))}
        </div>
      </div>

      {hasAnyDue ? (
        <GanttChart
          tasks={tasks}
          gantt={ganttQ.data}
          externals={externals}
          columns={boardQ.data.columns}
          window={win}
          now={now}
        />
      ) : (
        <EmptyState
          icon={<Icon name="gantt" size={40} />}
          title="Нет задач со сроками"
          description="Проставьте дедлайны на доске, чтобы видеть план во времени."
          action={
            <Link to="/boards/$boardId" params={{ boardId }} className={btnSecondarySm}>
              <Icon name="kanban" size={14} />К доске
            </Link>
          }
        />
      )}
    </div>
  )
}
